import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import AvatarInfoCard from '@ui/components/common/AvatarInfoCard';
import { PrimaryGrid } from '@ui/components/common/Grid';
import SearchFilter from '@ui/components/Button/SearchFilter';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { EventTypeEnum } from '@application/enums/event-type-enum';
import { getAllParkingSlotsAsync } from '@application/features/familyFest/familyFestParkingSlotThunk';
import { FamilyFestComingWithEnum } from '@application/enums/family-fest-registration-enums';
const ParkingContent = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [filters, setFilters] = useState({});
    const [gridFilterOpen, setGridFilterOpen] = useState(false);
    const [resetFiltersKey, setResetFiltersKey] = useState(0);
    const { parkingSlots } = useAppSelector((state) => state.familyFestParkingSlot);
    const VdkEventForDetails = useAppSelector((state) => state.VDKEvent.VDKEventsForEventDetails);
    const isFamilyFest = VdkEventForDetails?.type === EventTypeEnum.Family;
    // Parking slots are scoped by the selected companies via the auth token,
    // so the list must be refetched whenever the sidebar company selection changes.
    const selectedCompanyIds = useAppSelector((state) => state.company.selectedCompanyId);
    const dispatch = useAppDispatch();
    const companyFilterOptions = useMemo(() => {
        return (VdkEventForDetails?.eventCompanies ?? []).map((company) => ({
            value: company.value,
            label: company.label
        }));
    }, [VdkEventForDetails?.eventCompanies]);
    // Grid column fields don't always match the backend's sort keys.
    const PARKING_SORT_FIELD_MAP = {
        handyCappedSpace: 'handicappedspaces'
    };
    const fetchParkingSlots = () => {
        if (!isFamilyFest)
            return;
        const sort = sortModel[0];
        const request = {
            pageNumber: paginationModel.page + 1,
            pageSize: paginationModel.pageSize,
            searchString: searchValue.trim() || undefined,
            comingWith: filters.comingWith,
            companyId: filters.companyName,
            name: filters.name,
            requiredParkingSpace: filters.requiredParkingSpace,
            handicappedSpaces: filters.handyCappedSpace,
            sortBy: sort ? (PARKING_SORT_FIELD_MAP[sort.field] ?? sort.field) : undefined,
            sortOrder: sort?.sort ?? 'desc'
        };
        dispatch(getAllParkingSlotsAsync(request))
            .unwrap();
    };
    useEffect(() => {
        fetchParkingSlots();
    }, [dispatch, isFamilyFest, paginationModel.page, paginationModel.pageSize, searchValue, filters, sortModel, selectedCompanyIds]);
    const rows = useMemo(() => parkingSlots?.parkingSlots.items ?? [], [parkingSlots]);
    const mapComingWith = (value) => {
        switch (value) {
            case FamilyFestComingWithEnum.Alone: return t('familyForm.comingWithAlone');
            case FamilyFestComingWithEnum.Guest: return t('familyForm.comingWithGuest');
            case FamilyFestComingWithEnum.Family: return t('familyForm.comingWithFamily');
            default: return value;
        }
    };
    const columns = [
        {
            field: 'name',
            headerName: t('events.eventDetails.transportTab.attendeeName'),
            flex: 1.2,
            filterable: true,
            type: 'string',
            minWidth: 180
        },
        {
            field: 'companyName',
            headerName: t('events.eventDetails.transportTab.company'),
            flex: 1.1,
            type: 'string',
            filterable: true,
            minWidth: 180
        },
        {
            field: 'comingWith',
            headerName: t('events.eventDetails.transportTab.comingWith'),
            flex: 0.9,
            filterable: true,
            type: 'string',
            minWidth: 150,
            renderCell: (params) => {
                const row = params.row;
                const isGuest = ['guest', 'introduction'].includes(String(mapComingWith(row.comingWith) ?? '')
                    .trim()
                    .toLowerCase());
                return (_jsxs(Box, { display: 'flex', flexDirection: 'column', justifyContent: 'center', children: [_jsx(Typography, { variant: 'body2', color: 'text.primary', children: mapComingWith(row.comingWith) }), Number(row.totalMembers) > 0 && (_jsx(Typography, { variant: 'caption', color: 'text.secondary', children: `${row.totalMembers} ${isGuest ? t('events.eventDetails.transportTab.member') : t('events.eventDetails.transportTab.members')}` }))] }));
            }
        },
        {
            field: 'requiredParkingSpace',
            headerName: t('events.eventDetails.transportTab.requiredParkingSpaces'),
            flex: 0.8,
            filterable: true,
            type: 'number',
            minWidth: 150,
            headerAlign: "center",
            align: 'center',
        },
        {
            field: 'handyCappedSpace',
            headerName: t('events.eventDetails.transportTab.handicappedSpaces'),
            flex: 0.8,
            filterable: true,
            type: 'number',
            minWidth: 140,
            headerAlign: "center",
            align: 'center',
        }
    ];
    return (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, children: [_jsxs(Grid, { container: true, spacing: 1.5, children: [_jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsx(AvatarInfoCard, { header: t('events.eventDetails.transportTab.requiredParkingSpaces'), labelIcon: _jsx(Icon, { icon: 'material-symbols:parking-sign-outline', width: 20, height: 20 }), subheader: String(parkingSlots?.totalRequiredParkingSpaces ?? 0) }) }), _jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsx(AvatarInfoCard, { header: t('events.eventDetails.transportTab.handicappedSpaces'), labelIcon: _jsx(Icon, { icon: 'tabler:wheelchair', width: 20, height: 20 }), subheader: String(parkingSlots?.totalHandicappedSpaces ?? 0) }) })] }), _jsx(PrimaryGrid, { columns: columns, rows: rows, showHeaderControls: false, showNewHeaderControls: true, onApplyFilters: (newFilters) => {
                    setFilters(newFilters);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, filterOptions: {
                    companyName: companyFilterOptions,
                    comingWith: [
                        { value: FamilyFestComingWithEnum.Guest, label: t('familyForm.comingWithGuest') },
                        { value: FamilyFestComingWithEnum.Family, label: t('familyForm.comingWithFamily') },
                        { value: FamilyFestComingWithEnum.Alone, label: t('familyForm.comingWithAlone') }
                    ]
                }, resetFiltersKey: resetFiltersKey, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, paginationMode: 'server', rowCount: parkingSlots?.parkingSlots.totalCount ?? 0, sortingMode: 'server', sortModel: sortModel, onSortModelChange: (model) => {
                    setSortModel(model);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, customButton: _jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: isSmallScreen ? undefined : 'center', flexDirection: isSmallScreen ? 'column' : 'row', gap: isSmallScreen ? 1.5 : 0, children: [_jsx(Typography, { variant: 'h4', children: t('events.eventDetails.transportTab.individualParkingSpaces') }), _jsx(Box, { children: _jsx(SearchFilter, { forceDesktopVariant: true, fullWidth: true, searchValue: searchValue, onSearchChange: setSearchValue, placeholder: t('placeholders.search'), onClear: () => {
                                    setSearchValue('');
                                }, isDatagrid: true, iconButtonSize: 'medium', onFilterClick: () => setGridFilterOpen(true), showFilterClearButton: Object.keys(filters).length > 0, handleFilterClear: () => {
                                    setFilters({});
                                    setResetFiltersKey((prev) => prev + 1);
                                }, filterLabel: t('users.filters') }) })] }), filterOpen: gridFilterOpen, onFilterOpenChange: setGridFilterOpen, maxHeight: 'calc(100dvh - 350px)', boxProps: { sx: { display: 'flex', flexDirection: 'column', gap: '12px' } } })] }));
};
export default ParkingContent;
